<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import draggable from 'vuedraggable'
import { showModal } from '@/Utils/modalBus'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import SiteAdminService, { isBulkUnsupported, featuredSourceSlides } from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { ACCESS_LABELS, ACCESS_OPTIONS, itemRef, formatPrice } from '@/Utils/siteAccess'
import { scheduleState, fmtDateTime } from '@/Utils/siteSchedule'
import { contentKind, contentKindLabel, castrSourceNoun, KIND_FILTER_OPTIONS } from '@/Utils/siteContentKind'
import { featuredSourceMessage } from '@/Utils/siteFeatured'
import { notifySite } from '@/Utils/siteToast'
import { isBroadcast } from '@/components/Sites/siteTemplates'
import { mainLiveItemId } from '@/Utils/siteBroadcastSchedule'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconHandleVertical from '@/components/icon/IconHandleVertical.vue'
import IconLock from '@/components/icon/IconLock.vue'
import SiteContentModal from '@/components/Sites/SiteContentModal.vue'
import SitePlanModal from '@/components/Sites/SitePlanModal.vue'
import SiteAccessChangeModal from '@/components/Sites/SiteAccessChangeModal.vue'

// Content › Library (round 5): search + filters, bulk select with Hide / Show /
// Move to category / Change access, drag handles for the site order
// (runtime `POST /content/reorder`). Visibility changes apply immediately —
// they are not part of the theme draft. Round 6: access is per item only;
// Paid needs a plan mapping (planIds travel with the change) and bulk edits
// go through runtime `POST /content/bulk` with a per-item PATCH fallback.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const store = useStore()
const admin = useSiteAdmin(() => props.siteId)

const loading = ref(true)
const contentModal = ref(null)
const planModal = ref(null)
const accessModal = ref(null)
const pendingDelete = ref(null)
const reordering = ref(false)

const items = computed(() => admin.content.value)
// Broadcast has no category nav (Recent services shows all recordings unless
// Settings › Broadcast names a category), so the Library hides categories.
const broadcast = computed(() => isBroadcast(props.site))
const broadcastSchedule = computed(() => {
	if (!broadcast.value) return null
	const s = admin.settings.value || {}
	return {
		serviceTimes: Array.isArray(s.broadcastServiceTimes) ? s.broadcastServiceTimes : [],
		timeZone: s.broadcastTimezone || 'UTC',
		liveItemId: mainLiveItemId(s.broadcastLiveItemId, items.value),
	}
})
const categoryName = (id) => admin.categories.value.find((c) => String(c.id) === String(id))?.name || `#${id}`
// Posters that did not load (expired CDN certificate, deleted Castr asset…)
// fall back to the type placeholder instead of the browser's broken-image icon.
const brokenPosters = ref({})
const posterFailed = (item) => { brokenPosters.value = { ...brokenPosters.value, [item.id]: item.poster } }
const showPoster = (item) => Boolean(item.poster) && brokenPosters.value[item.id] !== item.poster

const scheduleBadge = (item) => {
	const state = scheduleState(item)
	if (state === 'upcoming') return { label: `Upcoming · ${fmtDateTime(item.countdown.startsAt)}`, variant: 'warning' }
	if (state === 'ended') return { label: 'Availability ended', variant: 'muted' }
	return null
}
const accessOf = (item) => item.access || 'public'
const accessVariant = (level) => (level === 'paid' ? 'warning' : level === 'registered' ? 'muted' : 'success')
const coveringLabel = (item) => {
	const plans = admin.plansCoveringItem(item)
	if (!plans.length) return 'no plan yet'
	return plans.slice(0, 2).map((p) => `${p.title} (${formatPrice(p)})`).join(', ') + (plans.length > 2 ? ` +${plans.length - 2}` : '')
}
const sourceLabel = (item) => {
	if (item.castr_ref) return `Castr ${castrSourceNoun(item.castr_ref)}`
	if (item.external_url) return 'External URL'
	return item.playback_url ? 'HLS' : 'No source'
}
const VISIBILITY_TIP = 'Applies on the site right away — visibility is not part of the theme draft.'
// Runtime `409 featured_source`: the item cannot be hidden while a featured
// slide opens it. Shown inline (with a link to Featured) instead of a toast.
const featuredBlock = ref(null)
/** Remember the block when `err` is a featured_source refusal; true when handled. */
const blockedByFeatured = (err, itemTitle) => {
	const slides = featuredSourceSlides(err)
	if (!slides) return false
	featuredBlock.value = featuredSourceMessage(slides, itemTitle)
	return true
}

// --- search & filters ---------------------------------------------------------------
const q = ref('')
const typeFilter = ref('')
const accessFilter = ref('')
const visibilityFilter = ref('')
// Type filter goes by what the item plays (a playlist is stored as `type: vod`).
const TYPE_OPTIONS = KIND_FILTER_OPTIONS
const ACCESS_FILTER_OPTIONS = [{ value: '', text: 'Any access' }, ...ACCESS_OPTIONS]
const VISIBILITY_OPTIONS = [{ value: '', text: 'Visible & hidden' }, { value: 'visible', text: 'Visible' }, { value: 'hidden', text: 'Hidden' }]
const filtering = computed(() => Boolean(q.value.trim() || typeFilter.value || accessFilter.value || visibilityFilter.value))
const visibleItems = computed(() => {
	const needle = q.value.trim().toLowerCase()
	return items.value.filter((item) => {
		if (typeFilter.value && contentKind(item) !== typeFilter.value) return false
		if (accessFilter.value && accessOf(item) !== accessFilter.value) return false
		if (visibilityFilter.value === 'visible' && item.published === false) return false
		if (visibilityFilter.value === 'hidden' && item.published !== false) return false
		if (needle) {
			const hay = [item.title, item.description, item.slug, ...(broadcast.value ? [] : (item.categoryIds || []).map(categoryName))].filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(needle)) return false
		}
		return true
	})
})
const clearFilters = () => {
	q.value = ''
	typeFilter.value = ''
	accessFilter.value = ''
	visibilityFilter.value = ''
}

// --- selection & bulk actions --------------------------------------------------------
const selected = ref(new Set())
const selectedItems = computed(() => items.value.filter((i) => selected.value.has(i.id)))
const allVisibleSelected = computed(() => visibleItems.value.length > 0 && visibleItems.value.every((i) => selected.value.has(i.id)))
const toggleSelect = (id, on) => {
	const next = new Set(selected.value)
	if (on) next.add(id)
	else next.delete(id)
	selected.value = next
}
const toggleSelectAll = (on) => {
	const next = new Set(selected.value)
	for (const item of visibleItems.value) {
		if (on) next.add(item.id)
		else next.delete(item.id)
	}
	selected.value = next
}
const clearSelection = () => { selected.value = new Set() }
// drop ids that disappeared (deleted / reloaded)
watch(items, (list) => {
	const ids = new Set(list.map((i) => i.id))
	if ([...selected.value].some((id) => !ids.has(id))) selected.value = new Set([...selected.value].filter((id) => ids.has(id)))
})

const bulkBusy = ref(false)
const bulkCategory = ref('')
const bulkCategoryOptions = computed(() => [
	{ value: '', text: 'Move to category…' },
	...admin.categories.value.map((c) => ({ value: String(c.id), text: `Add to “${c.name}”` })),
	{ value: '__none__', text: 'Remove from all categories' },
])

// Remembered once the runtime answered 404/405/501 for `POST /content/bulk`
// (sites-admin-api.md v0.10 — not on every runtime yet).
let bulkRouteMissing = false

/**
 * Apply one change to `targets`: runtime `POST /content/bulk { ids, ...change }`
 * when available (`{ results: [{ id, ok, item?, error? }], updated, failed }` —
 * each id is patched independently), else one PATCH per item with `patch(item)`
 * (null = nothing to do for that item). Returns `{ ok, failed, error }` and
 * refreshes the rows.
 */
const applyChange = async (targets, change, patch) => {
	let ok = 0
	let failed = 0
	let error = null
	if (change && !bulkRouteMissing) {
		try {
			const res = await SiteAdminService.bulkUpdateContent(props.siteId, { ids: targets.map((i) => i.id), ...change })
			const results = Array.isArray(res?.results) ? res.results : null
			if (results) {
				const byId = new Map(results.filter((r) => r.ok && r.item).map((r) => [r.item.id ?? r.id, r.item]))
				admin.content.value = admin.content.value.map((i) => (byId.has(i.id) ? { ...i, ...byId.get(i.id) } : i))
				const bad = results.filter((r) => !r.ok)
				const okCount = Number.isFinite(res.updated) ? res.updated : results.length - bad.length
				let error = null
				if (bad.length) {
					error = new Error(bad[0].error === 'validation_failed' ? 'the runtime rejected the change' : bad[0].error || 'update failed')
					// A per-item `featured_source` refusal keeps its slides (and the item) for the inline message.
					error.reason = bad[0].error || null
					error.details = bad[0].details && typeof bad[0].details === 'object' ? bad[0].details : null
					error.itemTitle = targets.find((i) => i.id === (bad[0].id ?? bad[0].item?.id))?.title || null
				}
				return { ok: okCount, failed: bad.length, error }
			}
			await admin.loadContent()
			return { ok: targets.length, failed: 0, error: null }
		} catch (err) {
			if (!isBulkUnsupported(err)) return { ok: 0, failed: targets.length, error: err }
			bulkRouteMissing = true
		}
	}
	for (const item of targets) {
		const body = patch(item)
		if (!body) { ok++; continue }
		try {
			const res = await SiteAdminService.updateContent(props.siteId, item.id, body)
			const updated = res?.item || { ...item, ...body }
			admin.content.value = admin.content.value.map((i) => (i.id === item.id ? { ...i, ...updated } : i))
			ok++
		} catch (err) {
			failed++
			error = err
			if (error && typeof error === 'object' && !error.itemTitle) error.itemTitle = item.title
		}
	}
	return { ok, failed, error }
}
/** Bulk-bar action on the selected rows; reports how many succeeded. */
const bulkPatch = async (label, change, patch) => {
	const targets = selectedItems.value
	if (!targets.length || bulkBusy.value) return
	bulkBusy.value = true
	featuredBlock.value = null
	const { ok, failed, error } = await applyChange(targets, change, patch)
	bulkBusy.value = false
	if (failed && blockedByFeatured(error, targets.length > 1 ? error?.itemTitle : null)) {
		notify({ group: 'error', text: `${label}: ${ok} updated, ${failed} ${failed === 1 ? 'is' : 'are'} the source of a featured slide.` })
	} else if (failed) notify({ group: 'error', text: `${label}: ${ok} updated, ${failed} failed${error?.message ? ` (${error.message})` : ''}.` })
	else notify({ group: 'success', text: `${label}: ${ok} ${ok === 1 ? 'item' : 'items'} updated.` })
	return { ok, failed }
}
const bulkSetPublished = (published) => bulkPatch(
	published ? 'Show on the site' : 'Hide from the site',
	{ hidden: !published },
	(item) => (item.published === published ? null : { published }),
)
const onBulkCategory = async (value) => {
	bulkCategory.value = ''
	if (!value) return
	if (value === '__none__') {
		await bulkPatch('Remove from categories', { categoryIds: [] }, (item) => ((item.categoryIds || []).length ? { categoryIds: [] } : null))
		return
	}
	// "Add to" keeps each item's other categories, so it is per item (bulk `categoryIds` replaces).
	const cat = admin.categories.value.find((c) => String(c.id) === String(value))
	await bulkPatch(`Add to “${cat?.name || value}”`, null, (item) => {
		const ids = (item.categoryIds || []).map(String)
		return ids.includes(String(value)) ? null : { categoryIds: [...(item.categoryIds || []), cat?.id ?? value] }
	})
}

// --- access (per row and bulk) ------------------------------------------------------
/** `scope: items` plans shared by every target today (preselected in the picker); empty when they differ. */
const commonPlanIds = (targets) => {
	const keys = targets.map((i) => [...admin.planIdsForRef(itemRef(i.id))].sort().join(','))
	return keys.length && keys.every((k) => k === keys[0]) && keys[0] ? keys[0].split(',') : []
}
const openAccess = (item) => accessModal.value?.show([item], { planIds: admin.planIdsForRef(itemRef(item.id)) })
const openBulkAccess = () => {
	const targets = selectedItems.value
	if (!targets.length || bulkBusy.value) return
	accessModal.value?.show(targets, { planIds: commonPlanIds(targets) })
}
/**
 * Paid → `planIds` is the full plan list for every item; anything else → `[]`
 * (the runtime clears the item's mapping). castr's products are updated to match
 * so the paywall and the site agree.
 */
const onAccessApply = async ({ items: targets, access, planIds }) => {
	if (access === 'paid' && !admin.canMonetize.value) {
		accessModal.value?.fail('Paid access needs a connected Stripe account. Connect Stripe under Settings › General first.')
		return
	}
	bulkBusy.value = true
	const before = [...new Set(targets.map(accessOf))]
	const from = before.length === 1 ? before[0] : 'mixed'
	const { ok, failed, error } = await applyChange(targets, { access, planIds }, () => ({ access, planIds }))
	if (ok && from !== access) trackSites(SITES_EVENTS.CONTENT_ACCESS_CHANGED, props.siteId, { from, to: access, items: ok })
	// The access change is done at this point; plan mapping problems are reported per plan, never as the raw paywall message.
	const mapping = ok ? await admin.applyPlanMembership(targets.map((i) => itemRef(i.id)), access === 'paid' ? planIds : []) : null
	bulkBusy.value = false
	if (failed) {
		accessModal.value?.fail(`${ok} of ${targets.length} updated, ${failed} failed${error?.message ? ` (${error.message})` : ''}.`)
		return
	}
	accessModal.value?.finish()
	// Same outlet as the plan notices so they stack instead of overlapping.
	if (admin.reportPlanMembership(mapping)) notifySite({ type: 'success', text: `${ACCESS_LABELS[access]}: ${ok} ${ok === 1 ? 'item' : 'items'} updated.` })
}

onMounted(async () => {
	loading.value = true
	// The Castr source picker reads live streams, VOD folders and playlists from the store.
	const pickerData = Promise.all([
		store.dispatch('Streams/getStreams').catch(() => null),
		store.dispatch('Playlist/getPlaylist').catch(() => null),
	])
	// Settings feed the "Open the player immediately" default of a new item (Theme › Player).
	await Promise.all([admin.loadContent(), admin.loadCategories(), admin.loadPlans(), admin.loadMonetization(), admin.loadSettings(), pickerData])
	loading.value = false
})
const onStripeConnected = () => Promise.all([admin.loadMonetization(), admin.loadPlans()])

const openCreate = () => contentModal.value?.show(null, [])
const openEdit = (item) => contentModal.value?.show(item, admin.planIdsForRef(itemRef(item.id)))

const onSaved = async ({ item, planIds }) => {
	await admin.loadContent()
	if (item?.id) admin.reportPlanMembership(await admin.applyPlanMembership(itemRef(item.id), planIds), { done: 'Content saved' })
}

const onCreatePlan = (refs) => planModal.value?.show(null, refs, { forItem: true })
// Existing rows: the refs are real item ids, nothing is pending. The access
// modal that opened this sets the rows to Paid itself on apply, so the plan
// modal does not offer to.
const onCreatePlanForRows = (refs) => planModal.value?.show(null, refs, { accessManaged: true })
// A plan created while the content / access modal is open is ticked there, so
// the item is mapped into it on save (new items only get their id after the POST).
const onPlanSaved = (product) => {
	admin.upsertPlan(product)
	contentModal.value?.selectPlan(product)
	accessModal.value?.selectPlan(product)
}

const requestDelete = (item) => {
	pendingDelete.value = item
	showModal('delete-site-content')
}
const confirmDelete = async () => {
	if (!pendingDelete.value) return
	try {
		await SiteAdminService.deleteContent(props.siteId, pendingDelete.value.id)
		notify({ group: 'success', text: `"${pendingDelete.value.title}" removed.` })
		await admin.loadContent()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not delete this item.' })
	}
	pendingDelete.value = null
}

// --- order ---------------------------------------------------------------------------
const persistOrder = async (ids) => {
	if (reordering.value) return
	reordering.value = true
	try {
		const res = await SiteAdminService.reorderContent(props.siteId, ids)
		if (res?.items) admin.content.value = res.items
		else await admin.loadContent()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not reorder.' })
		await admin.loadContent()
	}
	reordering.value = false
}
const orderedList = computed({
	get: () => visibleItems.value,
	set: (list) => {
		const currentIds = items.value.map((i) => i.id)
		const nextIds = list.map((i) => i.id)
		if (currentIds.join(',') === nextIds.join(',')) return
		// optimistic — vuedraggable already moved the DOM node
		admin.content.value = list
		persistOrder(nextIds)
	},
})
/** Keyboard fallback for the drag handle: Alt + ↑ / ↓ moves the focused row. */
const moveBy = (item, delta) => {
	if (filtering.value) return
	const ids = items.value.map((i) => i.id)
	const index = ids.indexOf(item.id)
	const target = index + delta
	if (index < 0 || target < 0 || target >= ids.length) return
	ids.splice(index, 1)
	ids.splice(target, 0, item.id)
	admin.content.value = ids.map((id) => items.value.find((i) => i.id === id))
	persistOrder(ids)
}

const togglePublished = async (item) => {
	featuredBlock.value = null
	try {
		const res = await SiteAdminService.updateContent(props.siteId, item.id, { published: !item.published })
		const updated = res?.item || { ...item, published: !item.published }
		admin.content.value = admin.content.value.map((i) => (i.id === item.id ? { ...i, ...updated } : i))
	} catch (err) {
		if (blockedByFeatured(err)) return
		notify({ group: 'error', text: err.message || 'Could not update visibility.' })
	}
}
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Library</h2>
				<p class="mb-0 text-tiny text-surface-7">Live channels and videos from your Castr account. Drag the handles to set the order on the site.</p>
			</div>
			<FormButton size="md" class="shrink-0" @click="openCreate">
				<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
				Add content
			</FormButton>
		</div>

		<Alert v-if="admin.errors.value.content" type="error" class="mb-4">{{ admin.errors.value.content.message }}</Alert>
		<Alert v-else-if="admin.errors.value.plans" type="normal" class="mb-4">
			Plans could not be loaded ({{ admin.errors.value.plans.message }}). Paid access can still be set; link plans once the Paywall connection is ready.
		</Alert>

		<Spinner v-if="loading" text="Loading content…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<EmptyState v-else-if="!items.length" title="No content yet" subtitle="Add a live stream or a video from your Castr library to start building the catalogue.">
			<template #actions>
				<FormButton class="w-64 justify-center" @click="openCreate">Add content</FormButton>
			</template>
		</EmptyState>

		<template v-else>
			<Alert v-if="featuredBlock" type="error" class="mb-3 items-center" data-testid="featured-source-block">
				<span class="min-w-0">
					{{ featuredBlock }}
					<router-link :to="{ name: 'SiteFeatured', params: { siteId } }" class="underline" data-testid="featured-source-link">Open Featured</router-link>
				</span>
				<FormButton type="link" size="sm" class="ml-auto shrink-0" @click="featuredBlock = null">Dismiss</FormButton>
			</Alert>

			<!-- Toolbar -->
			<div class="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 p-2.5" data-testid="content-toolbar">
				<div class="flex h-8 items-center px-1.5" :title="allVisibleSelected ? 'Clear selection' : 'Select all shown'">
					<FormCheckbox name="content-select-all" :input-value="true" :model-value="allVisibleSelected" :indeterminate="selected.size > 0 && !allVisibleSelected" :disabled="!visibleItems.length" data-testid="content-select-all" @update:model-value="toggleSelectAll($event)" />
					<span class="sr-only">Select all shown items</span>
				</div>
				<div class="relative flex min-w-[10rem] flex-1 items-center sm:max-w-xs">
					<icon-base class="pointer-events-none absolute left-2.5 z-10 text-surface-7" :width="16" :height="16"><icon-search /></icon-base>
					<label for="content-search" class="sr-only">Search content</label>
					<FormInput id="content-search" v-model="q" :placeholder="broadcast ? 'Search title or description…' : 'Search title, description or category…'" size="sm" class="w-full" custom-classes="!pl-10" />
				</div>
				<label for="content-filter-type" class="sr-only">Filter by type</label>
				<FormSelect id="content-filter-type" v-model="typeFilter" :options="TYPE_OPTIONS" size="sm" class="!w-32" />
				<label for="content-filter-access" class="sr-only">Filter by access</label>
				<FormSelect id="content-filter-access" v-model="accessFilter" :options="ACCESS_FILTER_OPTIONS" size="sm" class="!w-40" />
				<label for="content-filter-visibility" class="sr-only">Filter by visibility</label>
				<FormSelect id="content-filter-visibility" v-model="visibilityFilter" :options="VISIBILITY_OPTIONS" size="sm" class="!w-40" />
				<span class="ml-auto text-tiny text-surface-7" data-testid="content-count">
					<template v-if="filtering">{{ visibleItems.length }} of {{ items.length }}</template>
					<template v-else>{{ items.length }} {{ items.length === 1 ? 'item' : 'items' }}</template>
				</span>
				<FormButton v-if="filtering" type="link" size="sm" data-testid="content-clear-filters" @click="clearFilters">Clear filters</FormButton>
			</div>

			<!-- Bulk bar -->
			<div v-if="selected.size" class="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-surface-4 bg-surface-3 px-3 py-2" data-testid="content-bulk-bar" :aria-busy="bulkBusy">
				<span class="text-sm font-medium text-white" data-testid="content-selected-count">{{ selected.size }} selected</span>
				<FormButton type="secondary" size="sm" :disabled="bulkBusy" :title="VISIBILITY_TIP" data-testid="bulk-hide" @click="bulkSetPublished(false)">Hide</FormButton>
				<FormButton type="secondary" size="sm" :disabled="bulkBusy" :title="VISIBILITY_TIP" data-testid="bulk-show" @click="bulkSetPublished(true)">Show</FormButton>
				<label v-if="!broadcast" for="bulk-category" class="sr-only">Move to category</label>
				<FormSelect v-if="!broadcast" id="bulk-category" :model-value="bulkCategory" :options="bulkCategoryOptions" size="sm" class="!w-52" :disabled="bulkBusy || !admin.categories.value.length" data-testid="bulk-category" @update:model-value="onBulkCategory" />
				<FormButton type="secondary" size="sm" :disabled="bulkBusy" data-testid="bulk-access" @click="openBulkAccess">
					<icon-base class="mr-1.5" :width="14" :height="14" fill="none"><icon-lock /></icon-base>
					Change access…
				</FormButton>
				<FormButton type="link" size="sm" class="ml-auto" :disabled="bulkBusy" data-testid="bulk-clear" @click="clearSelection">Clear</FormButton>
			</div>

			<p v-if="filtering" class="mb-2 text-tiny text-surface-7" data-testid="content-reorder-locked">Reordering is off while the list is filtered — clear the filters to change the site order.</p>

			<EmptyState v-if="!visibleItems.length" title="Nothing matches" subtitle="Try another search or clear the filters." />

			<draggable
				v-else
				v-model="orderedList"
				tag="ul"
				class="m-0 flex list-none flex-col gap-2 p-0"
				data-testid="content-list"
				:item-key="(item) => item.id"
				handle=".content-drag-handle"
				:disabled="filtering || reordering"
				:delay="150"
				:delay-on-touch-only="true"
				ghost-class="opacity-40"
			>
				<template #item="{ element: item }">
					<li class="rounded-lg bg-surface-2" :class="selected.has(item.id) ? 'ring-1 ring-inset ring-main' : ''" :data-testid="`content-row-${item.id}`">
						<!--
							Under sm the row is a grid: handle / checkbox / thumbnail span the
							title + meta rows, the badges and then the actions take full-width
							rows of their own (Trello Dzyr44aL: beside ~140 px of actions the
							title was one letter and the badges sat on the buttons). From sm up
							the title block and badges are the single-line flex row as before —
							`contents` on the wrappers is what lets one DOM serve both.
						-->
						<div class="grid grid-cols-[auto_auto_auto_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1.5 px-3 py-3 sm:flex sm:gap-3">
							<button
								type="button"
								class="content-drag-handle col-start-1 row-span-2 row-start-1 shrink-0 touch-none rounded p-1 text-surface-7 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
								:class="filtering ? '' : 'cursor-grab active:cursor-grabbing'"
								:disabled="filtering || reordering"
								:title="filtering ? 'Clear the filters to reorder' : 'Drag to reorder (Alt + ↑/↓ with the keyboard)'"
								aria-label="Reorder"
								data-testid="content-drag-handle"
								@keydown.alt.up.prevent="moveBy(item, -1)"
								@keydown.alt.down.prevent="moveBy(item, 1)"
							>
								<icon-base :width="14" :height="14"><icon-handle-vertical /></icon-base>
							</button>
							<div class="col-start-2 row-span-2 row-start-1 flex shrink-0 items-center">
								<FormCheckbox :name="`content-select-${item.id}`" :input-value="true" :model-value="selected.has(item.id)" :data-testid="`content-select-${item.id}`" @update:model-value="toggleSelect(item.id, $event)" />
								<span class="sr-only">Select {{ item.title }}</span>
							</div>
							<img v-if="showPoster(item)" :src="item.poster" alt="" class="col-start-3 row-span-2 row-start-1 h-9 w-16 shrink-0 rounded object-cover bg-surface-3 sm:h-11 sm:w-20" :data-testid="`content-poster-${item.id}`" @error="posterFailed(item)">
							<div v-else class="col-start-3 row-span-2 row-start-1 grid h-9 w-16 shrink-0 place-items-center rounded bg-surface-3 text-tiny uppercase text-surface-7 sm:h-11 sm:w-20" :data-testid="`content-poster-placeholder-${item.id}`">{{ contentKindLabel(item) }}</div>
							<div class="contents sm:block sm:min-w-0 sm:flex-1">
								<div class="contents sm:flex sm:flex-wrap sm:items-center sm:gap-2">
									<h3 class="col-start-4 row-start-1 mb-0 min-w-0 truncate text-sm font-medium text-white">{{ item.title }}</h3>
									<div class="col-span-4 row-start-3 flex flex-wrap items-center gap-1.5 sm:contents" :data-testid="`content-badges-${item.id}`">
										<StatusPill pill size="sm" :variant="accessVariant(accessOf(item))" :data-testid="`content-access-${item.id}`">{{ ACCESS_LABELS[accessOf(item)] }}</StatusPill>
										<StatusPill v-if="!item.published" pill size="sm" variant="muted">Hidden</StatusPill>
										<StatusPill v-if="scheduleBadge(item)" pill size="sm" :variant="scheduleBadge(item).variant" :data-testid="`schedule-badge-${item.id}`">{{ scheduleBadge(item).label }}</StatusPill>
									</div>
								</div>
								<p class="col-start-4 row-start-2 mb-0 min-w-0 truncate text-tiny text-surface-7">
									<span :data-testid="`content-kind-${item.id}`">{{ contentKindLabel(item) }}</span> · {{ sourceLabel(item) }}
									<template v-if="!broadcast && item.categoryIds?.length"> · {{ item.categoryIds.map(categoryName).join(', ') }}</template>
									<template v-if="accessOf(item) === 'paid'"> · unlocked by {{ coveringLabel(item) }}</template>
								</p>
							</div>
							<div class="col-span-4 row-start-4 flex shrink-0 items-center justify-end gap-1" :data-testid="`content-actions-${item.id}`">
								<FormButton type="secondary" size="sm" :title="VISIBILITY_TIP" @click="togglePublished(item)">{{ item.published ? 'Hide' : 'Show' }}</FormButton>
								<FormButton is-icon type="secondary" size="sm" title="Change who can watch" :data-testid="`content-access-btn-${item.id}`" @click="openAccess(item)">
									<icon-base :width="16" :height="16" fill="none"><icon-lock /></icon-base>
								</FormButton>
								<FormButton is-icon type="secondary" size="sm" title="Edit" @click="openEdit(item)">
									<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
								</FormButton>
								<FormButton is-icon type="danger" size="sm" title="Delete" @click="requestDelete(item)">
									<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
								</FormButton>
							</div>
						</div>
					</li>
				</template>
			</draggable>
		</template>

		<SiteContentModal ref="contentModal" :site-id="siteId" :categories="admin.categories.value" :plans="admin.sellablePlans.value"
			:can-monetize="admin.canMonetize.value" :stripe="admin.stripe.value" :player-open-immediately="admin.playerOpenImmediately.value" :paid-badge-label="admin.premiumBadgeLabel.value"
			:broadcast="broadcastSchedule" @saved="onSaved" @create-plan="onCreatePlan" @stripe-connected="onStripeConnected" />
		<SiteAccessChangeModal ref="accessModal" :plans="admin.sellablePlans.value" :can-monetize="admin.canMonetize.value" :stripe="admin.stripe.value"
			@apply="onAccessApply" @create-plan="onCreatePlanForRows" @stripe-connected="onStripeConnected" />
		<SitePlanModal ref="planModal" :site-id="siteId" :content="admin.content.value"
			:can-monetize="admin.canMonetize.value" :stripe="admin.stripe.value" :capabilities="admin.planCapabilities.value" @saved="onPlanSaved" @stripe-connected="onStripeConnected" />
		<ConfirmModal modal-id="delete-site-content" modal-type="danger" ok-text="Delete" cancel-text="Cancel"
			title="Delete this content?" :message="`&quot;${pendingDelete?.title || ''}&quot; is removed from the site, including its featured and plan links.`"
			@modal-confirm="confirmDelete" />
	</div>
</template>
